import { showError, showSuccess } from '@calendly/alert'
import { useNewCode } from '@calendly/feature-check-react'
import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'

import { useGetOrganizationUuid, useGetUserQuery } from 'common'
import {
  EXTERNAL_RECAP_SHARING_FLAG,
  isRecapPublic,
  isRecapViewer,
  normalizeRecapAccessLevel,
  RECAP_ACCESS_LEVELS,
} from 'features/recaps/constants'
import { RECIPIENT_KINDS } from 'features/recaps/modals/share_modal/constants'
import {
  buildNewRecipientsPayload,
  countEffectiveRecipients,
  organizationRelationToDisplay,
  relationToDisplay,
  suggestionToPendingRecipient,
} from 'features/recaps/modals/share_modal/utils'
import { dispatchSegmentEvent } from 'features/tracked_meetings'
import { useTrack } from 'hooks/analytics_events'
import { isCalendlyForeignEventProvider } from 'shared/utils'

import { emptyPendingPermissions, emptyPendingRecipients } from './constants'
import { useAccessLevel } from './use_access_level'
import { useAccessList } from './use_access_list'
import { useSubmitPermissions } from './use_submit_permissions'

export { emptyPendingPermissions } from './constants'

const getForeignEventProviderFromSource = (source) => {
  if (!source) return null
  if (source.toUpperCase() === 'CALENDLY') return 'Calendly'

  return `${source.charAt(0)}${source.slice(1).toLowerCase()}`
}

const RecapShareContext = createContext(null)

export const useRecapShare = () => {
  const context = useContext(RecapShareContext)

  if (!context) {
    throw new Error('useRecapShare must be used within a RecapShareProvider')
  }

  return context
}

export const RecapShareProvider = ({
  children,
  trackedMeeting,
  recapPermission,
  recapShare = null,
  isRecapShareLoading = false,
  owner = null,
  templateSummaries,
}) => {
  const dispatch = useDispatch()
  const orgUuid = useGetOrganizationUuid()
  const { data: currentUser } = useGetUserQuery()
  const track = useTrack()
  const trackedMeetingUuid = trackedMeeting?.meetingIntelligence?.uuid
  const foreignEventId = trackedMeeting?.foreignEventId
  const foreignEventProvider = trackedMeeting?.foreignEventProvider
    ?? getForeignEventProviderFromSource(trackedMeeting?.source)
  const ownerUserUuid = owner?.uuid
  const [isShareModalOpen, openShareModal, closeShareModal] = useBooleanState(false)
  const [isCloseConfirmationOpen, openCloseConfirmation, dismissCloseConfirmation] = useBooleanState(false)
  const [isEmailModalOpen, openEmailModal, closeEmailModal] = useBooleanState(false)
  const [pendingPermissionUpdates, setPendingPermissionUpdates] = useState(emptyPendingPermissions)
  const [pendingRecipients, setPendingRecipients] = useState(emptyPendingRecipients)
  const [isAccessListModified, setIsAccessListModified] = useState(false)
  const [localShare, setLocalShare] = useState(undefined)

  const markAccessListModified = useCallback(() => {
    setIsAccessListModified(true)
  }, [])

  const clearAccessListModified = useCallback(() => {
    setIsAccessListModified(false)
  }, [])
  const [showInvalidEmailError, setShowInvalidEmailError] = useState(false)

  const hasLocalShareOverride = localShare !== undefined
  const share = hasLocalShareOverride ? localShare : recapShare
  const hasExternalRecapSharing = useNewCode(EXTERNAL_RECAP_SHARING_FLAG)?.enabled
  const isShareReady = !isRecapShareLoading && !isRecapViewer(recapPermission)

  useEffect(() => {
    setLocalShare(undefined)
  }, [recapShare])

  const accessLevel = useAccessLevel({
    trackedMeetingUuid,
    recapPermission,
    share,
    isShareModalOpen,
    isShareReady,
    setLocalShare,
  })

  const {
    accessList,
    recipientCount,
    setRecipientCount,
    refetch,
    applyPostMutationResult,
    applyDeleteMutationResult,
  } = useAccessList({
    isShareModalOpen,
    accessLevel: accessLevel.value,
    trackedMeetingUuid,
    recapPermission,
    owner,
    currentUser,
    orgUuid,
    hasExternalRecapSharing,
  })

  const { submit: submitPermissions, isSubmitting: isSubmittingPermissions } = useSubmitPermissions({
    trackedMeetingUuid,
    pendingPermissionUpdates,
    pendingRecipients,
    accessList,
    setPendingPermissionUpdates,
    setPendingRecipients,
    applyPostMutationResult,
    applyDeleteMutationResult,
  })

  // Clear pending permissions and recipients on modal close.
  useEffect(() => {
    if (!isShareModalOpen) {
      setPendingPermissionUpdates(emptyPendingPermissions)
      setPendingRecipients(emptyPendingRecipients)
      clearAccessListModified()
      dismissCloseConfirmation()
      setShowInvalidEmailError(false)
    }
  }, [isShareModalOpen, clearAccessListModified, dismissCloseConfirmation])

  const requestCloseShareModal = useCallback(() => {
    if (isAccessListModified) {
      openCloseConfirmation()
      return
    }

    closeShareModal()
  }, [closeShareModal, isAccessListModified, openCloseConfirmation])

  const confirmDiscardAndClose = useCallback(() => {
    dismissCloseConfirmation()
    closeShareModal()
  }, [closeShareModal, dismissCloseConfirmation])

  const closeShareModalAfterSave = useCallback(() => {
    setPendingPermissionUpdates(emptyPendingPermissions)
    setPendingRecipients(emptyPendingRecipients)
    clearAccessListModified()
    dismissCloseConfirmation()
    closeShareModal()
  }, [clearAccessListModified, closeShareModal, dismissCloseConfirmation])

  // Clear pending permissions and recipients when access level changes from Restricted.
  useEffect(() => {
    if (!isShareModalOpen || accessLevel.value == null) return

    if (accessLevel.value !== RECAP_ACCESS_LEVELS.RESTRICTED) {
      setPendingPermissionUpdates(emptyPendingPermissions)
      setPendingRecipients(emptyPendingRecipients)
      clearAccessListModified()
      // Clear cached access list so org relation doesn't persist when switching to PUBLIC/PRIVATE
      refetch()
    }
  }, [accessLevel.value, clearAccessListModified, isShareModalOpen, refetch])

  const setPendingUserPermission = useCallback((uuid, relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      users: {
        ...current.users,
        [uuid]: relation,
      },
    }))
    markAccessListModified()
  }, [markAccessListModified])

  const setPendingUserEmailPermission = useCallback((email, relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      userEmails: {
        ...current.userEmails,
        [email]: relation,
      },
    }))
    markAccessListModified()
  }, [markAccessListModified])

  const setPendingGroupPermission = useCallback((uuid, relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      userGroups: {
        ...current.userGroups,
        [uuid]: relation,
      },
    }))
    markAccessListModified()
  }, [markAccessListModified])

  const setPendingOrgPermission = useCallback((relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      organization: relation,
    }))
    markAccessListModified()
  }, [markAccessListModified])

  const getUserRelation = useCallback((uuid, serverRelation) => (
    relationToDisplay(pendingPermissionUpdates.users, uuid, serverRelation)
  ), [pendingPermissionUpdates.users])

  const getUserEmailRelation = useCallback((email, serverRelation) => (
    relationToDisplay(pendingPermissionUpdates.userEmails, email, serverRelation)
  ), [pendingPermissionUpdates.userEmails])

  const getUserGroupRelation = useCallback((uuid, serverRelation) => (
    relationToDisplay(pendingPermissionUpdates.userGroups, uuid, serverRelation)
  ), [pendingPermissionUpdates.userGroups])

  const getOrganizationRelation = useCallback((serverRelation) => (
    organizationRelationToDisplay(pendingPermissionUpdates.organization, serverRelation)
  ), [pendingPermissionUpdates.organization])

  const addRecipient = useCallback((suggestion) => {
    const recipient = suggestionToPendingRecipient(suggestion)

    if (!recipient) {
      return
    }

    setPendingPermissionUpdates((current) => {
      if (recipient.kind === RECIPIENT_KINDS.USER && Object.hasOwn(current.users, recipient.uuid)) {
        const users = { ...current.users }
        delete users[recipient.uuid]
        return { ...current, users }
      }

      if (recipient.kind === RECIPIENT_KINDS.USER_GROUP && Object.hasOwn(current.userGroups, recipient.uuid)) {
        const userGroups = { ...current.userGroups }
        delete userGroups[recipient.uuid]
        return { ...current, userGroups }
      }

      return current
    })

    setPendingRecipients((current) => {
      if (current.some(({ id, uuid, kind }) => (
        id === recipient.id || (uuid === recipient.uuid && kind === recipient.kind)
      ))) {
        return current
      }

      return [...current, recipient]
    })

    markAccessListModified()
  }, [markAccessListModified])

  const addEmailRecipient = useCallback((recipient) => {
    if (!recipient || recipient.kind !== RECIPIENT_KINDS.USER_EMAIL) {
      return
    }

    setPendingRecipients((current) => {
      if (current.some(({ kind, email }) => (
        kind === RECIPIENT_KINDS.USER_EMAIL && email === recipient.email
      ))) {
        // The recipient is already in the pending recipients list.
        return current
      }

      return [...current, recipient]
    })

    markAccessListModified()
  }, [markAccessListModified])

  const removeRecipient = useCallback((id) => {
    setPendingRecipients((current) => current.filter((recipient) => recipient.id !== id))
  }, [])

  const hasInvalidPendingRecipients = useMemo(() => (
    pendingRecipients.some(({ kind, isValidEmail }) => (
      kind === RECIPIENT_KINDS.USER_EMAIL && isValidEmail === false
    ))
  ), [pendingRecipients])

  useEffect(() => {
    if (!hasInvalidPendingRecipients) {
      setShowInvalidEmailError(false)
    }
  }, [hasInvalidPendingRecipients])

  const effectiveRecipientCount = useMemo(() => {
    const visibleUsers = accessList.internalUsers.filter((user) => (
      getUserRelation(user.uuid, user.relation) !== null
    ))
    const visibleUserGroups = accessList.userGroups.filter((userGroup) => (
      getUserGroupRelation(userGroup.uuid, userGroup.relation) !== null
    ))
    const visibleExternalUsers = (accessList.externalUsers ?? []).filter((user) => (
      getUserEmailRelation(user.email, user.relation) !== null
    ))
    const visiblePendingInvites = (accessList.pendingInvites ?? []).filter((invite) => (
      getUserEmailRelation(invite.email, invite.relation) !== null
    ))

    return countEffectiveRecipients({
      users: visibleUsers,
      userGroups: visibleUserGroups,
      ownerUserUuid,
      pendingRecipients,
      externalUsers: visibleExternalUsers,
      pendingInvites: visiblePendingInvites,
    })
  }, [
    accessList.externalUsers,
    accessList.pendingInvites,
    accessList.userGroups,
    accessList.internalUsers,
    getUserEmailRelation,
    getUserGroupRelation,
    getUserRelation,
    ownerUserUuid,
    pendingRecipients,
  ])

  const shareLinkUrl = useMemo(() => {
    const origin = window.location.origin
    const hasShareAccessLevel = Boolean(share?.accessLevel)
    const hasResolvedShareState = hasLocalShareOverride || hasShareAccessLevel || !isRecapShareLoading

    if (!hasResolvedShareState) {
      return null
    }

    const shareAccessLevel = normalizeRecapAccessLevel(share?.accessLevel)
    if (isRecapPublic(share?.accessLevel)) {
      return `${origin}/s/meetings/v2/${share?.uuid}`
    }

    if (shareAccessLevel === RECAP_ACCESS_LEVELS.RESTRICTED) {
      return `${origin}/s/tracked_meetings/r/${share?.uuid}`
    }

    return null
  }, [hasLocalShareOverride, share, isRecapShareLoading])

  const trackUserSharedSummary = useCallback((shareMethod) => {
    dispatchSegmentEvent(
      track,
      currentUser,
      foreignEventId,
      isCalendlyForeignEventProvider(foreignEventProvider),
      'user_shared_summary',
      trackedMeetingUuid,
      { share_uuid: share?.uuid, share_method: shareMethod }
    )
  }, [
    track,
    currentUser,
    foreignEventId,
    foreignEventProvider,
    trackedMeetingUuid,
    share?.uuid,
  ])

  const handleCopyShareLink = useCallback(async () => {
    if (!shareLinkUrl) {
      dispatch(showError(I18n.t('publisher.meeting_recap.recap_actions.link_copy_failed')))
      return
    }

    try {
      await navigator.clipboard.writeText(shareLinkUrl)
      dispatch(showSuccess(I18n.t('publisher.action.copied_to_clipboard')))
      trackUserSharedSummary('copy')
    } catch {
      dispatch(showError(I18n.t('publisher.meeting_recap.recap_actions.link_copy_failed')))
    }
  }, [shareLinkUrl, dispatch, trackUserSharedSummary])

  const openEmailFromShare = useCallback(() => {
    closeShareModal()
    openEmailModal()
  }, [closeShareModal, openEmailModal])

  const exportedShare = useMemo(() => {
    if (!share?.accessLevel) {
      return null
    }

    return share
  }, [share])

  const value = useMemo(() => ({
    trackedMeeting: {
      uuid: trackedMeetingUuid,
      name: trackedMeeting?.name,
    },
    owner,
    recapPermission,
    currentUser,
    hasExternalRecapSharing,
    templateSummaries,
    share: exportedShare,
    shareModal: {
      isOpen: isShareModalOpen,
      open: openShareModal,
      close: requestCloseShareModal,
      closeAfterSave: closeShareModalAfterSave,
      isCloseConfirmationOpen,
      dismissCloseConfirmation,
      confirmDiscardAndClose,
    },
    emailModal: {
      isOpen: isEmailModalOpen,
      open: openEmailFromShare,
      close: closeEmailModal,
    },
    accessLevel,
    accessList,
    pendingPermissions: pendingPermissionUpdates,
    pendingRecipients,
    permissionUpdates: {
      setPendingUserPermission,
      setPendingUserEmailPermission,
      setPendingGroupPermission,
      setPendingOrgPermission,
      getUserRelation,
      getUserEmailRelation,
      getUserGroupRelation,
      getOrganizationRelation,
    },
    recipientActions: {
      addRecipient,
      addEmailRecipient,
      removeRecipient,
    },
    validation: {
      hasInvalidPendingRecipients,
      showInvalidEmailError,
      setShowInvalidEmailError,
    },
    shareLink: {
      url: shareLinkUrl,
      copy: handleCopyShareLink,
    },
    tracking: {
      trackUserSharedSummary,
    },
    recipients: {
      count: effectiveRecipientCount,
      serverCount: recipientCount,
      setCount: setRecipientCount,
      newRecipientsPayload: buildNewRecipientsPayload(pendingRecipients),
    },
    submission: {
      submit: submitPermissions,
      isSubmitting: isSubmittingPermissions,
    },
  }), [
    trackedMeetingUuid,
    trackedMeeting?.name,
    owner,
    recapPermission,
    currentUser,
    hasExternalRecapSharing,
    templateSummaries,
    exportedShare,
    isShareModalOpen,
    openShareModal,
    requestCloseShareModal,
    closeShareModalAfterSave,
    isCloseConfirmationOpen,
    dismissCloseConfirmation,
    confirmDiscardAndClose,
    isEmailModalOpen,
    openEmailFromShare,
    closeEmailModal,
    accessLevel,
    accessList,
    pendingPermissionUpdates,
    pendingRecipients,
    setPendingUserPermission,
    setPendingUserEmailPermission,
    setPendingGroupPermission,
    setPendingOrgPermission,
    getUserRelation,
    getUserEmailRelation,
    getUserGroupRelation,
    getOrganizationRelation,
    addRecipient,
    addEmailRecipient,
    removeRecipient,
    hasInvalidPendingRecipients,
    showInvalidEmailError,
    setShowInvalidEmailError,
    shareLinkUrl,
    handleCopyShareLink,
    trackUserSharedSummary,
    effectiveRecipientCount,
    recipientCount,
    setRecipientCount,
    submitPermissions,
    isSubmittingPermissions,
  ])

  return (
    <RecapShareContext.Provider value={value}>
      {children}
    </RecapShareContext.Provider>
  )
}
